import * as React from 'react';

/** Names a combobox input through `aria-labelledby` by its non-wrapping `<label>`s, re-linked after every render, since Base UI hides the label while the popup is open and a rebuilt label (one gaining a hint) has no id. */
export function useAssociatedLabelIds(enabled: boolean): (input: HTMLInputElement | null) => void {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const enabledRef = React.useRef(enabled);
  // The value this hook last set, so a caller's own `aria-labelledby` is never cleared.
  const ownValueRef = React.useRef<string | null>(null);
  const link = React.useCallback(() => {
    const input = inputRef.current;
    if (input === null || input.id === '') return;
    const ids = enabledRef.current
      ? Array.from(input.labels ?? [])
          .filter((label) => !label.contains(input))
          .map((label, index) => {
            if (label.id === '')
              label.id = index === 0 ? `${input.id}-label` : `${input.id}-label-${index}`;
            return label.id;
          })
      : [];
    if (ids.length > 0) {
      ownValueRef.current = ids.join(' ');
      input.setAttribute('aria-labelledby', ownValueRef.current);
    } else if (ownValueRef.current !== null) {
      if (input.getAttribute('aria-labelledby') === ownValueRef.current) {
        input.removeAttribute('aria-labelledby');
      }
      ownValueRef.current = null;
    }
  }, []);
  React.useLayoutEffect(() => {
    enabledRef.current = enabled;
    link();
  });
  return React.useCallback(
    (input: HTMLInputElement | null) => {
      inputRef.current = input;
      link();
    },
    [link],
  );
}
